// api
import type { LocationDto } from '@/api/generated/recalc/endpoints/v2/locations';

// relative
import type { DaluxBuilding } from './types';

function normalizeMatchValue(value: string | number | null | undefined) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getMatchTokens(value: string | number | null | undefined) {
  return normalizeMatchValue(value)
    .split(' ')
    .filter((token) => token.length > 1);
}

export function getDaluxBuildingAddress(
  building?: DaluxBuilding | null,
): string {
  if (!building?.data?.address) {
    return '';
  }

  return [
    [building.data.address.road, building.data.address.number]
      .filter(Boolean)
      .join(' '),
    [building.data.address.zipCode, building.data.address.city]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ');
}

export function getDaluxBuildingLabel(building?: DaluxBuilding | null): string {
  return (
    [
      building?.data?.name,
      building?.data?.alternativeName,
      getDaluxBuildingAddress(building),
    ]
      .filter(Boolean)
      .join(' – ') ||
    building?.data?.buildingId ||
    ''
  );
}

export function getRemoniLocationLabel(location?: LocationDto | null): string {
  if (!location) {
    return '';
  }

  const streetLine = [
    location.displayName,
    [location.street, location.houseNumber].filter(Boolean).join(' '),
    [location.zipCode, location.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  return streetLine;
}

export function getRemoniLocationAddress(
  location?: LocationDto | null,
): string {
  if (!location) {
    return '';
  }

  return [
    [location.street, location.houseNumber].filter(Boolean).join(' '),
    [location.zipCode, location.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
}

export function getDaluxBuildingsMatchingRemoniLocation({
  daluxBuildings = [],
  remoniLocation,
}: {
  daluxBuildings?: DaluxBuilding[];
  remoniLocation?: LocationDto | null;
}): DaluxBuilding[] {
  const normalizedAddress = normalizeMatchValue(
    getRemoniLocationAddress(remoniLocation),
  );

  if (!normalizedAddress) {
    return daluxBuildings;
  }

  const addressTokens = getMatchTokens(normalizedAddress);

  return daluxBuildings.filter((building) => {
    const normalizedBuildingAddress = normalizeMatchValue(
      getDaluxBuildingAddress(building),
    );

    if (!normalizedBuildingAddress) {
      return false;
    }

    if (normalizedBuildingAddress === normalizedAddress) {
      return true;
    }

    return (
      addressTokens.length > 0 &&
      addressTokens.every((token) => normalizedBuildingAddress.includes(token))
    );
  });
}
